
import {
  computed,
  defineComponent,
  getCurrentInstance,
  nextTick,
  onMounted,
  watch,
  ref
} from 'vue'
import debounce from 'lodash/debounce'
import scrollIntoView from 'scroll-into-view-if-needed'

import { CellMouseEvent, getCursor, getTable } from './types'
import { getSelectedColumnIndexes, getSelectedRowIndexes } from './utils'

export default defineComponent({
  setup() {
    updateCursorModelOnEvents()
    makeSureCursorStayInside()
    updateCursorCoordinationOnModelChanged()
    const { style } = setupCursorStyle()
    const { updateCursorCoordination } =
      updateCursorCoordinationOnModelChanged()
    const cursor = getCursor()
    const $table = getTable()
    const vm = getCurrentInstance()?.proxy

    const errorMessage = computed((): string => {
      const row = $table.rows[cursor.rowIndex]
      const column = $table.columns[cursor.columnIndex]
      const errors = row?.$errors?.[column?.name]

      if (errors) {
        return errors.join(', ')
      }
      return ''
    })
    watch(style, () => {
      // Find the <tr> that currently has the 'active-row' class and remove it
      const currentActiveRow = $table.$el.querySelector('tr.active-row')
      if (currentActiveRow) {
        currentActiveRow.classList.remove('active-row')
      }

      // Select the <tr> with data-index equal to cursor.rowIndex and add the 'active-row' class
      const newActiveRow = $table.$el.querySelector(
        `tr[data-index="${cursor.rowIndex}"]`
      )
      if (newActiveRow) {
        newActiveRow.classList.add('active-row')
      }
    })
    const dataTooltip = computed((): any => {
      let isShow = false
      let text = ''
      if (cursor.rowIndex > -1) {
        const row = $table.rows[cursor.rowIndex]
        const column = $table.columns[cursor.columnIndex]
        if (!row || !column) return { isShow, text }
        text = row[column?.name]
        isShow = column.showTooltip && text
      }
      return {
        isShow,
        text
      }
    })
    // 行が1件だけの場合、その行（画面上の唯一のデータ）を隠さずに
    // ツールチップをスクロールして見せる余裕がない。
    // そのためこのパターンのテーブルでは、代わりにactivator方式の
    // ツールチップを使う（テンプレート側を参照）。
    // TODO: これは行数で判定している暫定対応であり、本来の条件
    // （スクロール領域に、行を隠さずツールチップを見せるだけの余白が
    // ない）そのものではない。2〜3行程度の短いテーブルでも同じ問題が
    // 起こり得るが、現状はこの条件では検知できない。実機（ブラウザ）で
    // 確認できる状況になったら、行数に関わらずactivator方式に統一し、
    // 下のattach+scrollIntoViewの処理を削除することを検討する。
    const isSingleRow = computed(() => $table.rows.length <= 1)
    const cellKey = computed(() => `${cursor.rowIndex}-${cursor.columnIndex}`)
    // 位置の基準は$refs.cursorではなく実際の<td>にする: カーソル枠自体の
    // 位置はupdateCursorCoordinationが書き込んでおり、これはdebounce
    // （非同期）されているため、新しいセルに移動した直後は一瞬、前の
    // （またはゼロの）座標のままになることがある。その状態でVuetifyが
    // activatorの位置を読んでツールチップを配置してしまう。セル自体は
    // ブラウザがネイティブにレイアウトするため、このような遅延がない。
    // そのため、あえてdebounceを経由せず、カーソル移動のたびにDOMを
    // 再取得している。
    const activatorCell = computed((): Element | null => {
      if (cursor.rowIndex < 0 || cursor.columnIndex < 0) return null
      return getCellElement($table, cursor.columnIndex, cursor.rowIndex)
    })
    // 1行用の2つのツールチップ共通で使う（テンプレート参照）:
    // :keyによってカーソル移動のたびに強制的に再マウントさせ、Vuetifyに
    // 新しいactivatorから位置を再計算させている。これがないと、
    // ツールチップが表示されたまま別のセルへ移動した場合（例: 同じ行の
    // 複数列にエラーがある場合）、位置が古いセルのまま固定されてしまう。
    const singleRowTooltipProps = computed(() => ({
      activator: activatorCell.value,
      openOnHover: false,
      openOnFocus: false,
      openOnClick: false,
      bottom: true,
      minWidth: 200,
      value: true
    }))
    const scrollTooltipIntoView = async () => {
      if (isSingleRow.value) return
      await nextTick()
      // Wait for the tooltip to be rendered
      await new Promise((resolve) => setTimeout(resolve, 100))
      requestAnimationFrame(() => {
        const tooltipEl = vm.$el?.querySelector('.v-tooltip__content') ?? vm.$el
        scrollIntoView(tooltipEl, { scrollMode: 'if-needed', block: 'nearest' })
      })
    }
    watch(errorMessage, (newVal) => {
      if (newVal) scrollTooltipIntoView()
    })
    watch(dataTooltip, (newVal) => {
      if (newVal.isShow) scrollTooltipIntoView()
    })
    onMounted(() => {
      document.getElementById('cursor').addEventListener('resizeCursor', () => {
        updateCursorCoordination()
      })
    })

    return {
      style,
      errorMessage,
      dataTooltip,
      isSingleRow,
      activatorCell,
      cellKey,
      singleRowTooltipProps
    }
  }
})

// A mouse click always targets a cell the user can already see, so the
// scroll-into-view correction in updateCursorCoordination is unnecessary
// for mouse-driven moves - and actually harmful, since it can shift
// scrollTop between the click's mousedown and click events and make the
// click miss its target (e.g. a checkbox). Keyboard navigation can move
// the cursor outside the visible area, so it still needs the correction.
let lastCursorMoveWasMouse = false

function updateCursorModelOnEvents() {
  const $table = getTable()
  const cursor = getCursor()

  $table.$on('cellclick', moveCursor)
  $table.$on('table.contextmenu', moveCursor)

  function moveCursor({ rowIndex, columnIndex, $event }: CellMouseEvent) {
    // Safe-guard
    if (!(rowIndex !== undefined && columnIndex !== undefined)) {
      return
    }

    // If user right-click inside a selection region, do not move the cursor
    if (
      $event.button === 2 &&
      getSelectedRowIndexes($table).includes(rowIndex) &&
      getSelectedColumnIndexes($table).includes(columnIndex)
    ) {
      return
    }

    if (columnIndex !== cursor.columnIndex || rowIndex !== cursor.rowIndex) {
      lastCursorMoveWasMouse = true
      cursor.columnIndex = columnIndex
      cursor.rowIndex = rowIndex
    }
  }
}

function makeSureCursorStayInside() {
  const $table = getTable()
  const cursor = getCursor()

  // Make sure cursor stay inside the table when the number of rows has changed
  watch(
    () => $table.rows.length,
    (newRowsLength) => {
      if (newRowsLength - 1 < cursor.rowIndex) {
        cursor.rowIndex = newRowsLength - 1
      }
    }
  )

  // Make sure cursor stay inside the table when the number of columns has changed
  watch(
    () => $table.columns.length,
    (newColumnsLength) => {
      if (newColumnsLength - 1 < cursor.columnIndex) {
        /* istanbul ignore next */
        cursor.columnIndex = newColumnsLength - 1
      }
    }
  )
}

function updateCursorCoordinationOnModelChanged() {
  const $table = getTable()
  const cursor = getCursor()
  const vm = getCurrentInstance()?.proxy
  const fisrtActiveIndex = $table.columns.findIndex(
    (col) => col.isActive === true
  )

  /**
   * Given a column and a row, get its corresponding cell's coordination
   */
  function getCellCoordination({ columnIndex, rowIndex }) {
    const cell = getCellElement($table, columnIndex, rowIndex) as HTMLElement
    if (!cell) return { width: 0, height: 0 }

    return {
      top: cell.offsetTop,
      left: cell.offsetLeft,
      width: cell.offsetWidth,
      height: cell.offsetHeight
    }
  }

  /**
   * Update current cursor coordination.
   * Due to it is called on both row and column changes, we need to debounce it so
   * it won't be fired twice.
   */
  const updateCursorCoordination = debounce(() => {
    const movedByMouse = lastCursorMoveWasMouse
    lastCursorMoveWasMouse = false

    // if(vm.$attrs.isCustomCursor){
    //   let currentColIndex = $table.cursor.columnIndex
    //   let tableElement = $table.$el;
    //   let tableContainer = tableElement.parentNode;
    //   let containerRect = tableContainer.getBoundingClientRect();

    //   let stickyElement = tableElement.querySelector(".stickyHeader")
    //   let vCardElement = document.getElementsByClassName("v-card__text")[0]
    //   let thElement = tableElement.querySelector("th")
    //   let thElementRect = thElement.getBoundingClientRect()
    //   let stickyElementSale = tableElement.querySelector(".isFixedHeaderSaleScreen")
    //   let stickyElementRect = stickyElement?.getBoundingClientRect();
    //   let stickyElementSaleRect = stickyElementSale?.getBoundingClientRect();

    //   let classNm = `.cell-${currentColIndex}-${cursor.rowIndex}`
    //   let cellElement = tableElement.querySelector(classNm);
    //   let cellRect = cellElement?.getBoundingClientRect();

    //   let {side, spaceAdjust} = checkHorizontalElement(cellRect, containerRect)

    //   if(side!=="" && currentColIndex !== fisrtActiveIndex){
    //     // Move scroll if not visible and isn't first col
    //     const currentTranslateX = getTranslateX(tableElement);
    //     const newTranslateX = side === "left" ? (currentTranslateX + spaceAdjust) : (currentTranslateX - spaceAdjust);
    //     tableElement.style.transform = `translateX(${newTranslateX}px)`;
    //   } else if(currentColIndex === fisrtActiveIndex){
    //     // if not visible and is first col move back to 0px
    //     tableElement.style.transform = `translateX(0px)`;
    //   }
    //   // Check top
    //   if(stickyElementRect?.bottom >= cellRect?.top){
    //     window.scrollTo(0, window.scrollY + cellRect?.top - stickyElementRect?.bottom)
    //   }
    //   // Check top in other not have sticky thead
    //   if(vCardElement && thElementRect?.bottom >= cellRect?.top){
    //     vCardElement.scrollTop= vCardElement.scrollTop + cellRect?.top - thElementRect?.bottom
    //   }
    //   // Check top in Sale
    //   if(stickyElementSaleRect && stickyElementSaleRect?.bottom >= cellRect?.top){
    //     window.scrollTo(0, window.scrollY + cellRect?.top - stickyElementSaleRect?.bottom)
    //   }
    // }
    const newCoord = getCellCoordination($table.cursor)
    Object.assign(cursor, newCoord)

    // The clicked cell is already visible on screen, so scrolling here would
    // only shift the layout under the pointer between mousedown and click
    // (e.g. making a checkbox miss the click that should toggle it).
    if (movedByMouse) return

    // Scroll into view after the new coord takes effect
    requestAnimationFrame(() => {
      scrollIntoView(vm.$el, { scrollMode: 'if-needed', block: 'nearest' })

      // scroll-into-view-if-needed aligns the cursor flush with the scroll
      // container's raw geometric top edge. It has no notion that a sticky
      // header visually paints over that same band, since position: sticky
      // is set on the <th> cells (not the <thead>/<tr>), so we must measure
      // an actual <th> here to get its real, pinned position - measuring the
      // <thead> itself reports its natural (non-sticky) scrolled-away spot.
      const scrollContainer = vm.$el.closest('.table-container')
      const headerCell = $table.$el.querySelector('thead th')
      if (!scrollContainer || !headerCell) return
      const overlap =
        headerCell.getBoundingClientRect().bottom -
        vm.$el.getBoundingClientRect().top
      if (overlap > 0) {
        scrollContainer.scrollTop -= overlap
      }
    })
  })

  /**
   * Whenever row and column of the cursor model has been changed,
   * update the cursor's coordinate
   */
  watch(
    [() => cursor.rowIndex, () => cursor.columnIndex],
    updateCursorCoordination
  )
  return {
    updateCursorCoordination
  }
}

function getCellElement(
  $table: ReturnType<typeof getTable>,
  columnIndex: number,
  rowIndex: number
): Element | null {
  return $table.$el.querySelector(`.cell-${columnIndex}-${rowIndex}`)
}

function checkHorizontalElement(elementRect, containerRect) {
  // check element is visible or not and return space if need to adjust
  let side = ''
  let spaceAdjust = 0
  if (elementRect?.left <= containerRect?.left) {
    spaceAdjust = containerRect?.left - elementRect?.left
    side = 'left'
  }
  if (elementRect?.right >= containerRect?.right) {
    spaceAdjust = elementRect?.right - containerRect?.right
    side = 'right'
  }
  return {
    side: side,
    spaceAdjust: spaceAdjust
  }
}
// Get position of element
function getTranslateX(element) {
  const transformValue = window
    .getComputedStyle(element)
    .getPropertyValue('transform')
  const matrix = new DOMMatrix(transformValue)
  return matrix.m41
}
/**
 * From the cursor's model, build its CSS style so it will show up where we want it to
 */
function setupCursorStyle() {
  const cursor = getCursor()

  return {
    style: computed(() => {
      // Hide the cursor when it is not on any cell yet
      if (!(cursor.width && cursor.height)) {
        return {
          display: 'none'
        }
      }

      return {
        width: `${cursor.width}px`,
        height: `${cursor.height}px`,
        transform: `translate(${cursor.left}px, ${cursor.top}px)`
      }
    })
  }
}
